export interface Character {
  name: string;
  role: string;
  emoji: string;
  skillUses: string[];
}

export interface SideQuest {
  description: string;
  skillUses: string[];
}

export interface Location {
  name: string;
  description: string;
  emoji: string;
  skillUses: string[];
}

export interface Skill {
  name: string;
  emoji: string;
  application?: string; // Concrete action description (e.g., "dodge thorns", "pick locks")
}

export interface Objective {
  description: string;
  skillUses: string[];
}

export interface Encounter {
  description: string;
  type?: string;
  skillUses: string[];
}

// Draft versions - section-wide suggestions
export interface SectionDrafts<T> {
  options: T[]; // Pool of suggestions (e.g., 8 items)
}

export interface StorySeedDrafts {
  storyName: SectionDrafts<string>;
  mainObjective: SectionDrafts<Objective>;
  storyTwist: SectionDrafts<string>;
  sideQuests: SectionDrafts<SideQuest>;
  encounters: SectionDrafts<Encounter>;
  skills: SectionDrafts<Skill>;
  characters: SectionDrafts<Character>;
  storyIntro: SectionDrafts<string>;
  locations: SectionDrafts<Location>;
  themeVariant: SectionDrafts<string>; // Step 2: theme variant descriptions
}

export interface StorySeed {
  storyName: string;
  mainObjective: Objective;
  storyTwist: string;
  sideQuests: SideQuest[];
  encounters: Encounter[];
  skills: Skill[];
  characters: Character[];
  storyIntro: string;
  locations: Location[];
}

export interface LockState {
  storyName: boolean;
  mainObjective: boolean;
  storyTwist: boolean;
  sideQuests: boolean[];
  encounters: boolean[];
  skills: boolean[];
  characters: boolean[];
  storyIntro: boolean;
  locations: boolean[];
}

export interface SlotCounts {
  sideQuests: number;
  encounters: number;
  characters: number;
  locations: number;
}

export type GenerationStep = 1 | 2 | 3 | 4;

export interface GeneratorSettings {
  language: string;
  ageGroup: string;
  theme: string;
  additionalInstructions: string;
  slotCounts: SlotCounts;
  generationStep: GenerationStep; // Current wizard step
  themeVariant: string; // Selected theme variant description (step 2)
  lockedSkills: Skill[]; // Skills locked in step 2 (mandatory)
  mainCharacterName: string;
}

export const DEFAULT_SLOT_COUNTS: SlotCounts = {
  sideQuests: 2,
  encounters: 3,
  characters: 3,
  locations: 3,
};

export const DEFAULT_LOCK_STATE: LockState = {
  storyName: false,
  mainObjective: false,
  storyTwist: false,
  sideQuests: [false, false],
  encounters: [false, false, false],
  skills: [false, false, false, false],
  characters: [false, false, false],
  storyIntro: false,
  locations: [false, false, false],
};

export const DEFAULT_SETTINGS: GeneratorSettings = {
  language: "English",
  ageGroup: "6",
  theme: "",
  additionalInstructions: "",
  slotCounts: DEFAULT_SLOT_COUNTS,
  generationStep: 1,
  themeVariant: "",
  lockedSkills: [],
  mainCharacterName: "",
};

const EMPTY_SIDE_QUEST: SideQuest = { description: "", skillUses: [] };
const EMPTY_CHARACTER: Character = { name: "", role: "", emoji: "", skillUses: [] };
const EMPTY_LOCATION: Location = { name: "", description: "", emoji: "", skillUses: [] };
const EMPTY_SKILL: Skill = { name: "", emoji: "" };
const EMPTY_OBJECTIVE: Objective = { description: "", skillUses: [] };
const EMPTY_ENCOUNTER: Encounter = { description: "", skillUses: [] };

export const EMPTY_STORY_SEED: StorySeed = {
  storyName: "",
  mainObjective: EMPTY_OBJECTIVE,
  storyTwist: "",
  sideQuests: [EMPTY_SIDE_QUEST, EMPTY_SIDE_QUEST],
  encounters: [EMPTY_ENCOUNTER, EMPTY_ENCOUNTER, EMPTY_ENCOUNTER],
  skills: [EMPTY_SKILL, EMPTY_SKILL, EMPTY_SKILL, EMPTY_SKILL],
  characters: [EMPTY_CHARACTER, EMPTY_CHARACTER, EMPTY_CHARACTER],
  storyIntro: "",
  locations: [EMPTY_LOCATION, EMPTY_LOCATION, EMPTY_LOCATION],
};

export const EMPTY_DRAFTS: StorySeedDrafts = {
  storyName: { options: [] },
  mainObjective: { options: [] },
  storyTwist: { options: [] },
  sideQuests: { options: [] },
  encounters: { options: [] },
  skills: { options: [] },
  characters: { options: [] },
  storyIntro: { options: [] },
  locations: { options: [] },
  themeVariant: { options: [] },
};

// Helper to create empty arrays for slot counts
export function createEmptyDraftsForCounts(_counts: SlotCounts): StorySeedDrafts {
  return {
    storyName: { options: [] },
    mainObjective: { options: [] },
    storyTwist: { options: [] },
    sideQuests: { options: [] },
    encounters: { options: [] },
    skills: { options: [] },
    characters: { options: [] },
    storyIntro: { options: [] },
    locations: { options: [] },
    themeVariant: { options: [] },
  };
}

export function createLockStateForCounts(counts: SlotCounts): LockState {
  return {
    storyName: false,
    mainObjective: false,
    storyTwist: false,
    sideQuests: Array.from({ length: counts.sideQuests }, () => false),
    encounters: Array.from({ length: counts.encounters }, () => false),
    skills: [false, false, false, false],
    characters: Array.from({ length: counts.characters }, () => false),
    storyIntro: false,
    locations: Array.from({ length: counts.locations }, () => false),
  };
}

export function createEmptySeedForCounts(counts: SlotCounts): StorySeed {
  return {
    storyName: "",
    mainObjective: { description: "", skillUses: [] },
    storyTwist: "",
    sideQuests: Array.from(
      { length: counts.sideQuests },
      () => ({ description: "", skillUses: [] }),
    ),
    encounters: Array.from(
      { length: counts.encounters },
      () => ({ description: "", skillUses: [] }),
    ),
    skills: Array.from({ length: 4 }, () => ({ name: "", emoji: "" })),
    characters: Array.from(
      { length: counts.characters },
      () => ({ name: "", role: "", emoji: "", skillUses: [] }),
    ),
    storyIntro: "",
    locations: Array.from(
      { length: counts.locations },
      () => ({ name: "", description: "", emoji: "", skillUses: [] }),
    ),
  };
}
